/**
 * @fileoverview Contact form contract — ContactFormValues shape, Zod validation
 *   schema, defaults, and the legacy-contact migration helper shared by every
 *   contact create/edit surface.
 * @module core-domain/forms/contactFormSchema
 *
 * Responsibilities:
 * - Define ContactFormValues + PhoneEntry/EmailEntry shapes and the
 *   contactValidationSchema enforced by all shared contact forms.
 * - Provide computeContactDefaults / create*Entry / migrateContact helpers.
 * Dependencies:
 * - zod; types/postalAddress (PostalAddress + evaluatePostalAddressFormat).
 * Architecture:
 * - Host-neutral package: pure schema + helpers, no Dexie or network access.
 * Notes:
 * - postalAddressSchema is intentionally tolerant (coerce/catch/passthrough)
 *   so imported/legacy mailing addresses lacking validationStatus remain
 *   editable instead of silently failing the whole form save.
 */
import { z } from 'zod';
import type { FormFieldDef } from './shared/fieldTypes';
import { EMAIL_REGEX, PHONE_REGEX } from './shared/validationHelpers';
import type { PostalAddress } from '../types/postalAddress';
import { evaluatePostalAddressFormat } from '../types/postalAddress';

// ---- Roles (single source of truth) ----

/**
 * Static seed fallback used while lookup values have not yet synced.
 * All role UI reads from the `entityContactRole` lookup category;
 * this array is the offline / initial-load fallback only.
 */
export const CONTACT_ROLES: readonly string[] = [
  'Property Owner',
  'Billing Party',
  'Tenant',
  'Emergency Contact',
  'Contractor',
];

/** Roles are dynamic lookup values — widened to string for runtime flexibility. */
export type ContactRole = string;

// ---- Phone / Email Types ----

export const PHONE_TYPES = ['Office', 'Mobile', 'Home', 'Emergency', 'Fax'] as const;
export type PhoneType = (typeof PHONE_TYPES)[number];

export const EMAIL_TYPES = ['Business', 'Personal', 'Emergency'] as const;
export type EmailType = (typeof EMAIL_TYPES)[number];

// ---- Sub-Entry Interfaces ----

export interface PhoneEntry {
  _id: string;
  type: PhoneType;
  number: string;
  extension?: string;
  isPrimary: boolean;
}

export interface EmailEntry {
  _id: string;
  type: EmailType;
  email: string;
  isPrimary: boolean;
}

// ---- Field Definitions (ordered) ----

export const CONTACT_FIELDS: FormFieldDef[] = [
  { key: 'name',         labelKey: 'name',         type: 'text',         required: true,  quickStart: true, order: 1, span: 2 },
  { key: 'defaultRoles', labelKey: 'defaultRoles', type: 'multi-select', required: false, quickStart: true, order: 2, span: 2 },
  { key: 'company',      labelKey: 'company',      type: 'text',         required: false, quickStart: true, order: 3, span: 1 },
  { key: 'title',        labelKey: 'title',        type: 'text',         required: false, quickStart: true, order: 4, span: 1 },
  { key: 'phoneNumbers', labelKey: 'phoneNumbers', type: 'phone-list',   required: false, quickStart: true, order: 5, span: 2 },
  { key: 'emails',       labelKey: 'emails',       type: 'email-list',   required: false, quickStart: true, order: 6, span: 2 },
  { key: 'address',      labelKey: 'address',      type: 'address',      required: false, quickStart: true, order: 7, span: 2 },
];

// ---- Form Data Shape ----

export interface ContactFormValues {
  name: string;
  firstName?: string;
  lastName?: string;
  defaultRoles: string[];
  company: string;
  title: string;
  phoneNumbers: PhoneEntry[];
  emails: EmailEntry[];
  /** Legacy freeform address line, retained for backward-compatible reads. */
  address: string;
  /**
   * Structured, validatable mailing address (supersedes `address`).
   * Tri-state: `undefined` = field untouched (leave stored value alone),
   * `null` = user explicitly cleared every field (persist the removal),
   * `PostalAddress` = set/replace.
   */
  mailingAddress?: PostalAddress | null;
  isOccupant?: boolean;
  isEmergency?: boolean;
  shouldReceiveReports?: boolean;
  notes?: string;
}

// ---- Validation (Zod) ----

const phoneEntrySchema = z.object({
  _id:       z.string(),
  type:      z.enum(PHONE_TYPES),
  number:    z.string().min(1, 'Phone number is required').regex(PHONE_REGEX, 'Invalid phone format'),
  extension: z.string().optional(),
  isPrimary: z.boolean(),
});

const emailEntrySchema = z.object({
  _id:       z.string(),
  type:      z.enum(EMAIL_TYPES),
  email:     z.string().min(1, 'Email is required').regex(EMAIL_REGEX, 'Invalid email format'),
  isPrimary: z.boolean(),
});

// Tolerant schema: legacy/imported mailing addresses (e.g. Regrid parcel-owner
// imports) may lack validationStatus/countryCode, carry non-string fields, or
// include vendor keys (careOf, urbanization, originalAddress). Coerce + default
// instead of rejecting so such contacts remain editable; StructuredAddressField
// always stamps a valid status for user-entered data.
//
// IMPORTANT: bare z.coerce.string() maps nullish input to the literal strings
// "undefined"/"null" — fabricated address components that would then persist.
// Every coerce goes through preprocess so missing/null fields yield '' (or
// undefined for optional keys), while real non-string values still coerce.
const coerceStr = z.preprocess((v) => (v == null ? '' : v), z.coerce.string());
// Blank strings must map to undefined, not 0 — Number('') === 0, so a legacy
// empty latitude/longitude would otherwise persist a false equator/prime-
// meridian coordinate.
const coerceNum = z.preprocess(
  (v) => (v == null || (typeof v === 'string' && v.trim() === '') ? undefined : v),
  z.coerce.number().optional(),
);

const postalAddressSchema = z.object({
  line1:             coerceStr,
  line2:             coerceStr.optional(),
  city:              coerceStr,
  state:             coerceStr,
  postalCode:        coerceStr,
  countryCode:       z.preprocess((v) => (v == null ? 'US' : v), z.coerce.string().default('US')),
  formattedAddress:  coerceStr.optional(),
  validationStatus:  z.enum(['needsValidation', 'validated', 'validationWarning']).catch('needsValidation'),
  validationMessage: coerceStr.optional(),
  validatedAt:       coerceStr.optional(),
  latitude:          coerceNum,
  longitude:         coerceNum,
}).passthrough();

export const contactValidationSchema = z.preprocess((val: any) => {
  if (val && typeof val === 'object') {
    const fn = typeof val.firstName === 'string' ? val.firstName.trim() : '';
    const ln = typeof val.lastName === 'string' ? val.lastName.trim() : '';
    const derivedName = [fn, ln].filter(Boolean).join(' ');
    const name = typeof val.name === 'string' && val.name.trim() ? val.name.trim() : derivedName;
    return { ...val, name, firstName: fn, lastName: ln };
  }
  return val;
}, z.object({
  name:         z.string().min(1, 'Name is required').transform((s: string) => s.trim()),
  firstName:    z.string().optional().default(''),
  lastName:     z.string().optional().default(''),
  defaultRoles: z.array(z.string().min(1)).optional().default([]),
  company:      z.string().optional().default(''),
  title:        z.string().optional().default(''),
  phoneNumbers: z.array(phoneEntrySchema).min(1, 'Phone number is required'),
  emails:       z.array(emailEntrySchema).min(1, 'Email is required'),
  address:      z.string().optional().default(''),
  // nullish: null is the explicit "user cleared the address" sentinel and
  // must survive validation so the write layer can persist the removal.
  mailingAddress: postalAddressSchema.nullish(),
  isOccupant:   z.boolean().optional().default(false),
  isEmergency:  z.boolean().optional().default(false),
  shouldReceiveReports: z.boolean().optional().default(true),
  notes:        z.string().optional().default(''),
}));

// ---- Default Computation ----

export function computeContactDefaults(prefillName?: string): ContactFormValues {
  const nameParts = (prefillName ?? '').trim().split(' ');
  const firstName = nameParts[0] ?? '';
  const lastName = nameParts.slice(1).join(' ') || '';
  return {
    name:         prefillName ?? '',
    firstName,
    lastName,
    defaultRoles: [],
    company:      '',
    title:        '',
    phoneNumbers: [{ _id: `ph-${crypto.randomUUID()}`, type: 'Office', number: '', isPrimary: true }],
    emails:       [{ _id: `em-${crypto.randomUUID()}`, type: 'Business', email: '', isPrimary: true }],
    address:      '',
    mailingAddress: undefined,
    isOccupant:   false,
    isEmergency:  false,
    shouldReceiveReports: true,
    notes:        '',
  };
}

// ---- Helpers ----

export function createPhoneEntry(type: PhoneType = 'Office', isPrimary = false): PhoneEntry {
  return { _id: `ph-${crypto.randomUUID()}`, type, number: '', isPrimary };
}

export function createEmailEntry(type: EmailType = 'Business', isPrimary = false): EmailEntry {
  return { _id: `em-${crypto.randomUUID()}`, type, email: '', isPrimary };
}

// ---- Shared Helper: create contact from form values ----

export function createContactFromForm(
  values: ContactFormValues,
  pool: { push: (c: Record<string, unknown>) => void },
): { _id: string } & ContactFormValues {
  const newContact = { _id: `ct-${Date.now()}`, ...values };
  pool.push(newContact);
  return newContact;
}

// ---- Migration: convert old flat contact → new shape ----

export function migrateContact(old: {
  _id: string;
  name: string;
  role?: string;
  company?: string;
  title?: string;
  phone?: string;
  email?: string;
  address?: string;
  mailingAddress?: PostalAddress;
  [key: string]: unknown;
}): { _id: string } & ContactFormValues {
  return {
    _id:          old._id,
    name:         old.name ?? '',
    defaultRoles: old.role ? [old.role] : [],
    company:      old.company ?? '',
    title:        old.title ?? '',
    phoneNumbers: old.phone
      ? [{ _id: `ph-${old._id}`, type: 'Office' as PhoneType, number: old.phone, isPrimary: true }]
      : [{ _id: `ph-${old._id}`, type: 'Office' as PhoneType, number: '', isPrimary: true }],
    emails: old.email
      ? [{ _id: `em-${old._id}`, type: 'Business' as EmailType, email: old.email, isPrimary: true }]
      : [{ _id: `em-${old._id}`, type: 'Business' as EmailType, email: '', isPrimary: true }],
    address: old.address ?? '',
    // Preserve an already-structured (possibly USPS-validated) mailingAddress;
    // only seed one from the legacy freeform string when none exists yet, so a
    // repeated load never downgrades a validated address to needsValidation.
    mailingAddress: old.mailingAddress
      ?? (old.address?.trim()
        ? evaluatePostalAddressFormat({ line1: old.address.trim() })
        : undefined),
  };
}
